import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaBullhorn, FaRobot, FaChartBar, FaLightbulb, FaFacebookF, FaGoogle, FaMailBulk, FaWhatsapp } from 'react-icons/fa';
import Section from '../components/common/Section';
import ServiceCard from '../components/common/ServiceCard';
import Card from '../components/common/Card';

const PageHeader = styled.div`
  background: ${({ theme }) => theme.colors.gradient.primary};
  padding: ${({ theme }) => `${theme.space['3xl']} 0`};
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/assets/images/pattern-dots.svg');
    background-size: cover;
    opacity: 0.1;
    z-index: 1;
  }
  
  .container {
    position: relative;
    z-index: 2;
  }
`;

const PageTitle = styled(motion.h1)`
  margin-bottom: ${({ theme }) => theme.space.md};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const PageDescription = styled(motion.p)`
  max-width: 700px;
  margin: 0 auto;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.7;
  font-family: ${({ theme }) => theme.fonts.body};
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  max-width: 600px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: ${({ theme }) => theme.fontSizes.lg};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const PlatformSection = styled.div`
  margin-top: ${({ theme }) => theme.space['2xl']};
`;

const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.space.lg};
  margin-top: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PlatformCard = styled(Card)`
  padding: ${({ theme }) => theme.space.lg};
  text-align: center;
  transition: all 0.3s ease;
  border-top: 4px solid transparent;
  
  &:hover {
    transform: translateY(-10px);
    border-top: 4px solid ${({ theme }) => theme.colors.accent};
  }
`;

const PlatformIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.dark};
  margin: 0 auto ${({ theme }) => theme.space.md};
  background: ${({ theme }) => theme.colors.gradient.accent};
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  box-shadow: ${({ theme }) => theme.shadows.neon};
  transition: all 0.5s ease;
  
  &:hover {
    transform: rotate(10deg) scale(1.05);
  }
`;

const PlatformTitle = styled.h4`
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

const PlatformDescription = styled.p`
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: 0;
`;

const services = [
  {
    icon: <FaBullhorn />,
    title: 'Digital Marketing',
    description: 'Strategic campaigns across Google, Facebook, WhatsApp, and Email platforms to maximize your brand reach and conversion rates.',
    linkTo: '/services/digital-marketing',
  },
  {
    icon: <FaRobot />,
    title: 'AI-Powered Analytics',
    description: 'Leverage machine learning algorithms for precise customer segmentation, behavior prediction, and data-driven forecasting.',
    linkTo: '/services/ai-analytics',
  },
  {
    icon: <FaChartBar />,
    title: 'Business Intelligence',
    description: 'Real-time data visualization through custom PowerBI and Tableau dashboards for informed business decisions.',
    linkTo: '/services/business-intelligence',
  },
  {
    icon: <FaLightbulb />,
    title: 'Strategic Consulting',
    description: 'Comprehensive market analysis and bespoke strategies to optimize customer journeys and marketing funnels.',
    linkTo: '/services/strategic-consulting',
  },
];

const platforms = [
  {
    icon: <FaGoogle />,
    title: 'Google Ads',
    description: 'Search, Display & YouTube campaigns to capture high-intent audiences.'
  },
  {
    icon: <FaFacebookF />,
    title: 'Facebook & Instagram',
    description: 'Highly targeted social campaigns with compelling visual content.'
  },
  {
    icon: <FaMailBulk />,
    title: 'Email Marketing',
    description: 'Personalized email sequences that nurture leads and drive conversions.'
  },
  {
    icon: <FaWhatsapp />,
    title: 'WhatsApp Marketing',
    description: 'Direct engagement with customers through conversational marketing.'
  },
];

const Services: React.FC = () => {
  return (
    <>
      <PageHeader>
        <div className="container">
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, type: 'spring', stiffness: 100 }}
          >Our Services</PageTitle>
          <PageDescription
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, type: 'spring', stiffness: 50 }}
          >
            Comprehensive digital transformation solutions powered by data analytics and AI, tailored to accelerate your business growth
          </PageDescription>
        </div>
      </PageHeader>
      
      <Section id="services">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ type: 'spring', stiffness: 100, damping: 15 }}
        >
          What We Offer
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ type: 'spring', stiffness: 100, damping: 15, delay: 0.2 }}
        >
          We provide innovative digital solutions powered by advanced analytics to transform your business
        </SectionSubtitle>
        
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              linkTo={service.linkTo}
              delay={0.2 * (index + 1)}
            />
          ))}
        </ServicesGrid>
        
        <PlatformSection>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
          >
            Digital Marketing Platforms
          </SectionTitle>
          
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            We specialize in creating high-impact campaigns across these platforms
          </SectionSubtitle>
          
          <PlatformGrid>
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ type: 'spring', stiffness: 100, damping: 15, delay: 0.15 * index }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <PlatformCard>
                  <PlatformIcon>{platform.icon}</PlatformIcon>
                  <PlatformTitle>{platform.title}</PlatformTitle>
                  <PlatformDescription>{platform.description}</PlatformDescription>
                </PlatformCard>
              </motion.div>
            ))}
          </PlatformGrid>
        </PlatformSection>
      </Section>
    </>
  );
};

export default Services;
